import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Plug } from 'lucide-react';
import { Sidebar } from '../../components/ui/sidebar';
import { PageHeader } from '../../components/ui/page-header/page-header';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { AgentHomeCards } from '../../components/ui/connections/agent-home-cards';
import { ApiKeysTab } from '../../components/ui/connections/api-keys-tab';
import {
  getConnectedAgentStatus,
  getEndpointAgentStatus,
} from '../../components/ui/connections/connected-agent-status';
import {
  type ConnectedAgentRow,
  ConnectedAgentsTable,
} from '../../components/ui/connections/connected-agents-table';
import {
  type EditedAgent,
  EndpointWizard,
} from '../../components/ui/connections/endpoint-wizard/endpoint-wizard';
import { ImportWizard } from '../../components/ui/connections/import-wizard/import-wizard';
import { useAgents } from '../../contexts/AgentContext';
import {
  draftFromConnection,
  isExternalAgent,
  retestSavedConnection,
} from '../../lib/external-agents/api';
import type { ConnectionFormat } from '../../lib/external-agents/types';
import { useClaudeConsole } from '../../lib/hooks/useClaudeConsole';
import { useExternalConnections } from '../../lib/hooks/useExternalConnections';
import { useManagedAgentImports } from '../../lib/hooks/useManagedAgentImports';
import {
  isManagedAgent,
  runConnectionCheck,
  updateManagedAgentVersion,
} from '../../lib/managed-agents/api';
import { useAuthStore } from '../../stores/authStore';

const FORMAT_LABELS: Record<ConnectionFormat, string> = {
  json: 'JSON',
  openai: 'OpenAI format',
  anthropic: 'Anthropic format',
};

type OpenWizard =
  | { kind: 'claude_console' }
  | { kind: 'own_api'; editing?: EditedAgent }
  | null;

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/** Agents that live outside PromptSafe, and the keys that reach them. */
export function Connections() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const workspaceId = user?.currentWorkspace?.workspace_id;
  const isAdmin = user?.currentWorkspace?.role?.name === 'admin';

  const { agents, deleteAgent, refreshAgents } = useAgents();
  const { imports, reload: reloadImports } = useManagedAgentImports(
    workspaceId,
    agents,
  );
  const { connections, reload: reloadConnections } = useExternalConnections(
    workspaceId,
    agents,
  );
  const { keyStatus, setKeyStatus, remote, error, reloadRemote } =
    useClaudeConsole(workspaceId);

  const [wizard, setWizard] = useState<OpenWizard>(null);
  // An open wizard holds the previous workspace's selections and secret.
  useEffect(() => {
    setWizard(null);
  }, [workspaceId]);
  const wizardRef = useRef<HTMLDivElement>(null);

  const rows: ConnectedAgentRow[] = useMemo(
    () =>
      agents.flatMap((agent): ConnectedAgentRow[] => {
        if (isManagedAgent(agent)) {
          const agentImport = imports[agent.id];
          return [
            {
              agent,
              kind: 'claude_console',
              version: agentImport
                ? `v${agentImport.anthropic_agent_version}`
                : '—',
              since: agentImport
                ? `Imported ${format(new Date(agentImport.imported_at), 'd MMM')}`
                : null,
              status: getConnectedAgentStatus(agentImport, keyStatus, remote),
            },
          ];
        }
        if (isExternalAgent(agent)) {
          const connection = connections[agent.id];
          return [
            {
              agent,
              kind: 'own_api',
              location: connection
                ? hostOf(connection.endpoint_url)
                : undefined,
              version: connection
                ? (connection.model ?? FORMAT_LABELS[connection.format])
                : '—',
              since: connection
                ? `Connected ${format(new Date(connection.created_at), 'd MMM')}`
                : null,
              status: getEndpointAgentStatus(connection),
            },
          ];
        }
        return [];
      }),
    [agents, imports, connections, keyStatus, remote],
  );

  const openWizard = (next: OpenWizard) => {
    setWizard(next);
    if (next?.kind === 'claude_console') reloadRemote();
    requestAnimationFrame(() =>
      wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  };

  const editEndpointAgent = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    const connection = connections[agentId];
    if (!agent || !connection) return;
    openWizard({
      kind: 'own_api',
      editing: {
        agentId,
        name: agent.name,
        greeting: agent.greeting,
        draft: draftFromConnection(connection),
      },
    });
  };

  const testAgent = async (row: ConnectedAgentRow) => {
    if (!workspaceId) return;
    if (row.kind === 'own_api') {
      const result = await retestSavedConnection(workspaceId, row.agent.id);
      reloadConnections();
      if (!result.ok) throw new Error(result.error);
      return;
    }
    const agentImport = imports[row.agent.id];
    if (!agentImport) return;
    await runConnectionCheck({
      workspaceId,
      anthropicAgentId: agentImport.anthropic_agent_id,
      environmentId: agentImport.environment_id,
      agentId: row.agent.id,
    });
    reloadImports();
  };

  // The simulation page picks agents from the shared list, so make sure a
  // just-saved agent is in it before going there.
  const runSimulation = async (agentId: string) => {
    await refreshAgents();
    navigate('/conversation-simulation', { state: { agentId } });
  };

  const refreshAfterSave = () => {
    void refreshAgents();
    reloadConnections();
    reloadImports();
  };

  const closeWizard = () => {
    setWizard(null);
    reloadConnections();
  };

  if (!workspaceId) return null;

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex min-h-full w-full flex-col">
          <PageHeader
            title="Connections"
            icon={
              <Plug className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />
          <div className="flex max-w-6xl flex-col gap-6 p-6">
            <p className="max-w-3xl text-sm text-prompt-safecomabbey">
              Test agents you built outside PromptSafe with your personas and
              evaluators.
            </p>

            <Tabs defaultValue="agents" className="flex flex-col gap-6">
              <TabsList className="self-start">
                <TabsTrigger value="agents">Your agents</TabsTrigger>
                <TabsTrigger value="keys">API keys</TabsTrigger>
              </TabsList>

              <TabsContent value="agents" className="mt-0 flex flex-col gap-8">
                {error && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                  </p>
                )}
                <AgentHomeCards
                  workspaceId={workspaceId}
                  keyStatus={keyStatus}
                  onImport={() => openWizard({ kind: 'claude_console' })}
                  onConnectEndpoint={() => openWizard({ kind: 'own_api' })}
                />

                <div ref={wizardRef} className="scroll-mt-4">
                  {wizard?.kind === 'claude_console' && keyStatus && (
                    <ImportWizard
                      workspaceId={workspaceId}
                      isAdmin={isAdmin}
                      keyStatus={keyStatus}
                      onKeyChange={setKeyStatus}
                      remote={remote}
                      remoteError={error}
                      onClose={closeWizard}
                      onSaved={refreshAfterSave}
                      onRunSimulation={runSimulation}
                    />
                  )}
                  {wizard?.kind === 'own_api' && (
                    <EndpointWizard
                      // A new key per agent resets the form when switching.
                      key={wizard.editing?.agentId ?? 'new'}
                      workspaceId={workspaceId}
                      editing={wizard.editing}
                      onClose={closeWizard}
                      onSaved={refreshAfterSave}
                      onRunSimulation={runSimulation}
                    />
                  )}
                </div>

                <ConnectedAgentsTable
                  rows={rows}
                  onRun={runSimulation}
                  onTest={testAgent}
                  onUpdate={async (agentId) => {
                    await updateManagedAgentVersion(workspaceId, agentId);
                    reloadRemote();
                    reloadImports();
                  }}
                  onEdit={editEndpointAgent}
                  onRemove={deleteAgent}
                />
              </TabsContent>

              <TabsContent value="keys" className="mt-0">
                <ApiKeysTab
                  workspaceId={workspaceId}
                  isAdmin={isAdmin}
                  keyStatus={keyStatus}
                  onKeyChange={setKeyStatus}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
